import { useSuspenseQuery } from "@apollo/client";
import { DataTable, PrimaryFullScreenSpinner } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import type { EburyTradeRowFragment } from "~/gql/client/graphql";

export const Route = createFileRoute("/__auth/ebury/trades")({
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment EburyTradeRow on EburyTradeDetails {
    __typename
    id
    status
    tradeType
    orderDate
    maturityDate
    feeAmount {
      amount
      currency
    }
    sellAmount
    sellCurrency
    buyAmount
    buyCurrency
    rate
    rateSymbol
    synthetic
    parentTradeId
    reference
    eburyClientId
    transactions {
      transactionId
      clientId
      accountId
      amount {
        amount
        currency
      }
      bookingDatetime
      creditDebitIndicator
      status
      valueDatetime
      additionalTransactionInformation
      transactionInformation
      transactionReference
      transactionType
    }
    ... on EburyFixedForwardDetails {
      initialMargin {
        amount
        currency
      }
      initialMarginPaid
      marginCall {
        amount
        currency
      }
      sellBalanceForDrawdown {
        amount
        currency
      }
      buyBalanceForDrawdown {
        amount
        currency
      }
    }
    ... on EburyWindowForwardDetails {
      initialMargin {
        amount
        currency
      }
      initialMarginPaid
      marginCall {
        amount
        currency
      }
      sellBalanceForDrawdown {
        amount
        currency
      }
      buyBalanceForDrawdown {
        amount
        currency
      }
    }
  }
`);

const allTradesDocument = graphql(`
  query AdminEburyAllTrades {
    eburyClients {
      id
      clientName
      trades {
        ...EburyTradeRow
      }
    }
  }
`);

type TradeRow = EburyTradeRowFragment & {
  clientName: string;
};

const allColumns: (ColumnDef<TradeRow> & { hideWhenCompact?: boolean })[] = [
  {
    accessorKey: "clientName",
    header: "Client",
    enableSorting: false,
  },
  {
    accessorKey: "id",
    header: "Trade ID",
    enableSorting: false,
  },
  {
    accessorKey: "tradeType",
    header: "Type",
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
  },
  {
    id: "sell",
    header: "Sell",
    cell: ({ row }) =>
      `${row.original.sellAmount} ${row.original.sellCurrency}`,
    enableSorting: false,
  },
  {
    id: "buy",
    header: "Buy",
    cell: ({ row }) => `${row.original.buyAmount} ${row.original.buyCurrency}`,
    enableSorting: false,
  },
  {
    accessorKey: "orderDate",
    header: "Order Date",
    enableSorting: false,
    hideWhenCompact: true,
  },
  {
    accessorKey: "maturityDate",
    header: "Maturity Date",
    cell: ({ row }) => new Date(row.original.maturityDate).toLocaleDateString(),
    enableSorting: false,
    hideWhenCompact: true,
  },
  {
    accessorKey: "rate",
    header: "Rate",
    cell: ({ row }) =>
      `${row.original.rate} ${row.original.rateSymbol ?? ""}`.trim(),
    enableSorting: false,
    hideWhenCompact: true,
  },
];

function View() {
  const [selected, setSelected] = useState<TradeRow | null>(null);

  const { data } = useSuspenseQuery(allTradesDocument);

  const rows: TradeRow[] = (data.eburyClients ?? []).flatMap((client) =>
    (client.trades ?? []).map((t) => ({
      ...t,
      clientName: client.clientName,
    })),
  );

  const columns = selected
    ? allColumns.filter((c) => !c.hideWhenCompact)
    : allColumns;

  return (
    <div className="flex min-h-0 gap-4">
      <div className="min-w-0 flex-1">
        <DataTable
          data={rows}
          columns={columns}
          activeRowId={selected?.id}
          onRowClick={(row) => {
            setSelected(row.original.id === selected?.id ? null : row.original);
          }}
          className="overflow-hidden border border-zinc-200"
        />
      </div>
      {selected && (
        <TradeDetailPane trade={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function TradeDetailPane({
  trade,
  onClose,
}: {
  trade: TradeRow;
  onClose: () => void;
}) {
  return (
    <div className="w-[480px] flex-shrink-0 overflow-auto border-l px-6 py-2">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold text-lg">Trade Detail</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-zinc-100"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="text-sm">
        <DetailRow label="Client" value={trade.clientName} />
        <DetailRow label="ID" value={trade.id} />
        <DetailRow label="Type" value={trade.tradeType} />
        <DetailRow label="Status" value={trade.status} />
        <DetailRow label="Order Date" value={trade.orderDate} />
        <DetailRow
          label="Maturity Date"
          value={new Date(trade.maturityDate).toLocaleDateString()}
        />
        <DetailRow
          label="Sell"
          value={`${trade.sellAmount} ${trade.sellCurrency}`}
        />
        <DetailRow
          label="Buy"
          value={`${trade.buyAmount} ${trade.buyCurrency}`}
        />
        <DetailRow
          label="Rate"
          value={`${trade.rate} ${trade.rateSymbol ?? ""}`.trim()}
        />
        <DetailRow
          label="Fee"
          value={`${trade.feeAmount.amount} ${trade.feeAmount.currency}`}
        />
        <DetailRow label="Synthetic" value={trade.synthetic ? "Yes" : "No"} />
        <DetailRow label="Parent Trade" value={trade.parentTradeId ?? "-"} />
        <DetailRow label="Reference" value={trade.reference ?? "-"} />
        <DetailRow label="Client ID" value={trade.eburyClientId} />

        {(trade.__typename === "EburyFixedForwardDetails" ||
          trade.__typename === "EburyWindowForwardDetails") && (
          <>
            <DetailRow
              label="Buy Balance"
              value={`${trade.buyBalanceForDrawdown.amount} ${trade.buyBalanceForDrawdown.currency}`}
            />
            <DetailRow
              label="Sell Balance"
              value={`${trade.sellBalanceForDrawdown.amount} ${trade.sellBalanceForDrawdown.currency}`}
            />
            <DetailRow
              label="Initial Margin"
              value={`${trade.initialMargin.amount} ${trade.initialMargin.currency}`}
            />
            <DetailRow
              label="Margin Paid"
              value={
                trade.initialMarginPaid === null
                  ? "-"
                  : trade.initialMarginPaid
                    ? "Yes"
                    : "No"
              }
            />
            <DetailRow
              label="Margin Call"
              value={`${trade.marginCall.amount} ${trade.marginCall.currency}`}
            />
          </>
        )}
      </div>
    </div>
  );
}
